import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Volume2, VolumeX, Eye, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { GoldenRingAvatar } from "./GoldenRingAvatar";
import { ProBadge } from "./ProBadge";
import { MUSIC_LIBRARY, getMusicPlayer } from "./MusicLibrary";
import { MentionSuggestions, applyMention } from "./MentionSuggestions";
import StoryOverlayLayer from "./story/StoryOverlayLayer";
import ReactionBlast from "./story/ReactionBlast";
import { fxStoryReaction } from "@/utils/gamefx";
import "./storyViewer.css";

const IMAGE_STORY_DURATION = 7000;
const LONG_PRESS_MS = 220;
const SWIPE_THRESHOLD = 50;
const TAP_MOVE_TOLERANCE = 10;
const REACTION_EMOJIS = ["🔥", "😍", "😂", "😮", "👏", "💯", "😢", "🌟"];

const getImageUrl = (url) => {
  if (!url) return "/images/default-avatar.png";
  return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const formatTimeLeft = (expiresAt) => {
  if (!expiresAt) return "";
  const diffMs = new Date(expiresAt) - new Date();
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return "<1m";
};

export const StoryViewer = ({ groups: initialGroups, initialUserIndex = 0, initialStoryIndex = 0, onClose, onStoriesChanged }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(initialGroups);
  const [userIndex, setUserIndex] = useState(initialUserIndex);
  const [storyIndex, setStoryIndex] = useState(() => {
    const g = initialGroups[initialUserIndex];
    return g && initialStoryIndex < g.stories.length ? initialStoryIndex : 0;
  });
  const [isMediaReady, setIsMediaReady] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [holding, setHolding] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyFocused, setReplyFocused] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [viewersList, setViewersList] = useState([]);
  const [stickerResults, setStickerResults] = useState({ results: {}, mine: {}, is_owner: false });
  const [questionSheet, setQuestionSheet] = useState(null);
  const [questionText, setQuestionText] = useState("");
  const [poppedEmoji, setPoppedEmoji] = useState(null);
  const [reactionSent, setReactionSent] = useState(false);
  const [burst, setBurst] = useState(null);
  const [adeLiked, setAdeLiked] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [shake, setShake] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const openComments = async () => {
    setShowComments(true);
    try {
      const res = await api.get(`/stories/${story.story_id}/comments`);
      setComments(res.data.comments || []);
    } catch {}
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      const res = await api.post(`/stories/${story.story_id}/comments`, { text: commentText.trim() });
      setComments((prev) => [...prev, res.data.comment]);
      setCommentText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore commento");
    } finally { setSendingComment(false); }
  };

  const shareStory = async (mode) => {
    const link = `${window.location.origin}/app/home?story=${story.story_id}`;
    const text = `Guarda la storia di ${group.user?.name || "un utente"} su ADE!`;
    if (mode === "native" && navigator.share) {
      try { await navigator.share({ title: "Storia su ADE", text, url: link }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(link); toast.success("Link copiato!"); }
      catch {
        const ta = document.createElement("textarea"); ta.value = link;
        document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); toast.success("Link copiato!"); } catch {}
        document.body.removeChild(ta);
      }
      if (mode === "chat") navigate("/app/messages");
    }
    setShowShare(false);
  };

  const viewerRef = useRef(null);
  const videoRef = useRef(null);
  const replyInputRef = useRef(null);
  const activeFillRef = useRef(null);
  const rafRef = useRef(null);
  const lastTickRef = useRef(null);
  const elapsedRef = useRef(0);
  const durationRef = useRef(IMAGE_STORY_DURATION);
  const pausedRef = useRef(false);
  const mediaReadyRef = useRef(false);
  const mediaErrorRef = useRef(false);
  const goNextRef = useRef(() => {});
  const viewedRef = useRef(new Set());
  const holdTimerRef = useRef(null);
  const pointerRef = useRef(null);
  const musicPlayerRef = useRef(getMusicPlayer());

  const group = groups[userIndex];
  const story = group?.stories?.[storyIndex];

  // Restore per-story ADE Like state (persisted server-side)
  useEffect(() => {
    setAdeLiked(!!story?.my_ade_like);
  }, [story?.story_id, story?.my_ade_like]);
  const paused = holding || replyFocused || showViewers || showComments || !!questionSheet;

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // Risultati degli sticker interattivi della storia corrente
  useEffect(() => {
    const sid = story?.story_id;
    const hasInteractive = ((story?.overlays?.elements) || []).some(
      (el) => el.type === "poll" || el.type === "question" || el.type === "slider"
    );
    if (!sid || !hasInteractive) { setStickerResults({ results: {}, mine: {}, is_owner: false }); return; }
    let alive = true;
    api.get(`/stories/${sid}/sticker-results`)
      .then((r) => { if (alive) setStickerResults(r.data || { results: {}, mine: {}, is_owner: false }); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.story_id]);
  useEffect(() => { mediaReadyRef.current = isMediaReady; }, [isMediaReady]);
  useEffect(() => { mediaErrorRef.current = mediaError; }, [mediaError]);

  // Guard: close if session ran out
  useEffect(() => {
    if (!group || !story) onClose();
  }, [group, story, onClose]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // === NAVIGATION (single source of truth) ===
  const goNext = useCallback(() => {
    const g = groups[userIndex];
    if (!g) { onClose(); return; }
    if (storyIndex < g.stories.length - 1) {
      setStoryIndex(storyIndex + 1);
    } else if (userIndex < groups.length - 1) {
      setUserIndex(userIndex + 1);
      setStoryIndex(0);
    } else {
      onClose();
    }
  }, [groups, userIndex, storyIndex, onClose]);

  const goPrev = useCallback(() => {
    if (storyIndex > 0) {
      setStoryIndex(storyIndex - 1);
    } else if (userIndex > 0) {
      const prevGroup = groups[userIndex - 1];
      setUserIndex(userIndex - 1);
      setStoryIndex(Math.max(0, prevGroup.stories.length - 1));
    }
  }, [groups, userIndex, storyIndex]);

  const nextUser = useCallback(() => {
    if (userIndex < groups.length - 1) {
      setUserIndex(userIndex + 1);
      setStoryIndex(0);
    } else {
      onClose();
    }
  }, [groups, userIndex, onClose]);

  const prevUser = useCallback(() => {
    if (userIndex > 0) {
      setUserIndex(userIndex - 1);
      setStoryIndex(0);
    }
  }, [userIndex]);

  useEffect(() => { goNextRef.current = goNext; }, [goNext]);

  // === PROGRESS ENGINE (rAF, no re-renders) ===
  useEffect(() => {
    setIsMediaReady(false);
    setMediaError(false);
    elapsedRef.current = 0;
    lastTickRef.current = null;
    durationRef.current = IMAGE_STORY_DURATION;
    if (activeFillRef.current) activeFillRef.current.style.transform = "scaleX(0)";

    const tick = (now) => {
      if (lastTickRef.current == null) lastTickRef.current = now;
      const dt = now - lastTickRef.current;
      lastTickRef.current = now;
      if (!pausedRef.current && mediaReadyRef.current && !mediaErrorRef.current) {
        elapsedRef.current += dt;
        const p = Math.min(1, elapsedRef.current / durationRef.current);
        if (activeFillRef.current) activeFillRef.current.style.transform = `scaleX(${p})`;
        if (p >= 1) { goNextRef.current(); return; }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [userIndex, storyIndex, groups]);

  // Auto-advance on media error after short delay
  useEffect(() => {
    if (!mediaError) return;
    const t = setTimeout(() => goNextRef.current(), 2500);
    return () => clearTimeout(t);
  }, [mediaError]);

  // === VIDEO play/pause sync ===
  useEffect(() => {
    const v = videoRef.current;
    if (!v || story?.media_type !== "video") return;
    if (paused || mediaError) {
      v.pause();
    } else if (isMediaReady) {
      v.play().catch(() => {
        v.muted = true;
        v.play().catch(() => {});
      });
    }
  }, [paused, isMediaReady, mediaError, story, userIndex, storyIndex]);

  // === MUSIC ===
  useEffect(() => {
    if (story?.music_id && !isMuted && !paused) {
      musicPlayerRef.current.start(story.music_id);
    } else {
      musicPlayerRef.current.stop();
    }
  }, [story, isMuted, paused]);

  useEffect(() => {
    const player = musicPlayerRef.current;
    return () => player.stop();
  }, []);

  // === VIEWS: record once per story (real backend, no duplicates) ===
  useEffect(() => {
    if (!story || !group) return;
    if (group.isMine) {
      api.get(`/stories/${story.story_id}/views`)
        .then((r) => { setViewCount(r.data.count || 0); setViewersList(r.data.views || []); })
        .catch(() => {});
    } else if (!viewedRef.current.has(story.story_id)) {
      viewedRef.current.add(story.story_id);
      api.post(`/stories/${story.story_id}/view`).catch(() => {});
    }
  }, [story, group]);

  // === PRELOAD adjacent stories ===
  useEffect(() => {
    const preload = (s) => {
      if (s && s.media_type !== "video") {
        const img = new Image();
        img.src = getImageUrl(s.media_url);
      }
    };
    const g = groups[userIndex];
    if (!g) return;
    preload(g.stories[storyIndex + 1] || groups[userIndex + 1]?.stories?.[0]);
    preload(storyIndex > 0 ? g.stories[storyIndex - 1] : groups[userIndex - 1]?.stories?.slice(-1)[0]);
  }, [userIndex, storyIndex, groups]);

  // === KEYBOARD (desktop) ===
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") { if (e.key === "Escape") e.target.blur(); return; }
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, onClose]);

  // === Body scroll lock while viewer open (iOS Safari keyboard shift fix) ===
  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  // === iOS keyboard: viewer follows the visual viewport (no page shift) ===
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const el = viewerRef.current;
    const update = () => {
      if (!el) return;
      el.style.height = `${vv.height}px`;
      el.style.transform = vv.offsetTop > 0 ? `translateY(${vv.offsetTop}px)` : "";
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      if (el) { el.style.height = ""; el.style.transform = ""; }
    };
  }, []);

  // === POINTER: tap / long-press / swipe ===
  const handlePointerDown = (e) => {
    pointerRef.current = { x: e.clientX, y: e.clientY, held: false };
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      if (pointerRef.current) pointerRef.current.held = true;
      setHolding(true);
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e) => {
    const start = pointerRef.current;
    if (!start || !holdTimerRef.current) return;
    const moved = Math.abs(e.clientX - start.x) > TAP_MOVE_TOLERANCE || Math.abs(e.clientY - start.y) > TAP_MOVE_TOLERANCE;
    if (moved) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; } // drag, not a hold
  };

  const clearHold = () => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    setHolding(false);
  };

  const handlePointerUp = (zone) => (e) => {
    const start = pointerRef.current;
    pointerRef.current = null;
    if (!start) { clearHold(); return; }
    const wasHeld = start.held;
    clearHold();
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    // Swipes take priority (work even after a hold-turned-drag)
    if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) nextUser(); else prevUser();
      return;
    }
    if (dy > 90 && Math.abs(dy) > Math.abs(dx)) { onClose(); return; } // swipe down: close
    if (wasHeld) return; // long press ended: no navigation
    if (Math.abs(dx) <= TAP_MOVE_TOLERANCE && Math.abs(dy) <= TAP_MOVE_TOLERANCE) {
      if (zone === "left") goPrev(); else goNext();
    }
  };

  const handlePointerCancel = () => {
    pointerRef.current = null;
    clearHold();
  };

  // === ACTIONS ===
  const handleStickerInteract = async (el, payload) => {
    if (!story) return;
    // aggiornamento ottimistico: la UI reagisce subito
    setStickerResults((prev) => {
      const results = { ...prev.results };
      const entry = { ...(results[el.id] || { type: el.type, total: 0 }) };
      if (el.type === "poll") {
        const counts = [...(entry.counts || new Array((el.options || []).length).fill(0))];
        if (prev.mine?.[el.id]?.option_index === undefined) entry.total = (entry.total || 0) + 1;
        counts[payload.option_index] = (counts[payload.option_index] || 0) + 1;
        entry.counts = counts;
      } else if (prev.mine?.[el.id] === undefined) {
        entry.total = (entry.total || 0) + 1;
      }
      results[el.id] = entry;
      return { ...prev, results, mine: { ...prev.mine, [el.id]: payload } };
    });
    try {
      await api.post(`/stories/${story.story_id}/sticker/${el.id}/interact`, payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Interazione non riuscita");
    }
  };

  const sendQuestionAnswer = async () => {
    const text = questionText.trim();
    if (!text || !questionSheet) return;
    await handleStickerInteract(questionSheet, { text });
    setQuestionSheet(null);
    setQuestionText("");
    toast.success("Risposta inviata");
  };

  const handleReaction = async (emoji) => {
    if (!story) return;
    // ADE Like è un toggle: secondo tap lo rimuove
    if (emoji === "ADE_LIKE" && (adeLiked || story.my_ade_like)) {
      setAdeLiked(false);
      story.my_ade_like = false;
      try {
        await api.post(`/stories/${story.story_id}/react`, { emoji });
      } catch (err) { /* visual-first */ }
      return;
    }
    if (reactionSent) return;
    const id = Date.now();
    // esplosione a pieno schermo + scossa della scena + audio/haptics
    setBurst({ id, emoji });
    setShake(true);
    setTimeout(() => setShake(false), 700);
    fxStoryReaction(emoji === "ADE_LIKE");
    setPoppedEmoji(emoji);
    setTimeout(() => setPoppedEmoji(null), 500);
    setReactionSent(true);
    setTimeout(() => setReactionSent(false), 2500);
    if (emoji === "ADE_LIKE") { story.my_ade_like = true; setAdeLiked(true); }
    else story.my_reaction = emoji;
    try {
      await api.post(`/stories/${story.story_id}/react`, { emoji });
    } catch (err) { /* visual-first */ }
  };

  const handleReply = async () => {
    const text = replyText.trim();
    if (!text || sendingReply || !story) return;
    setSendingReply(true);
    try {
      await api.post(`/chat/${group.user.user_id}`, {
        content: text,
        story_ref: { story_id: story.story_id, media_url: story.media_url },
      });
      setReplyText("");
      replyInputRef.current?.blur();
      toast.success("Risposta inviata!");
    } catch (err) {
      toast.error("Errore nell'invio");
    } finally {
      setSendingReply(false);
    }
  };

  const handleDelete = async () => {
    if (!story) return;
    const sid = story.story_id;
    try {
      await api.delete(`/stories/${sid}`);
      toast.success("Mood eliminato");
      onStoriesChanged?.();
      const updated = groups
        .map((g, gi) => (gi === userIndex ? { ...g, stories: g.stories.filter((s) => s.story_id !== sid) } : g))
        .filter((g) => g.stories.length > 0);
      if (updated.length === 0) { onClose(); return; }
      const stillHere = updated[userIndex] && updated[userIndex].user.user_id === group.user.user_id;
      setGroups(updated);
      if (stillHere) {
        setStoryIndex(Math.min(storyIndex, updated[userIndex].stories.length - 1));
      } else {
        setUserIndex(Math.min(userIndex, updated.length - 1));
        setStoryIndex(0);
      }
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openProfile = () => {
    if (group.isMine) return;
    onClose();
    navigate(`/app/user/${group.user.user_id}`);
  };

  if (!group || !story) return null;

  const isVideo = story.media_type === "video";

  return createPortal(
    <div className="story-viewer" ref={viewerRef} role="dialog" aria-modal="true" data-testid="story-viewer">
      <div className={`story-canvas${shake ? " rb-shake" : ""}`}>
        {/* MEDIA */}
        {mediaError ? (
          <div className="story-error" data-testid="story-media-error">
            <ImageOff className="w-10 h-10" />
            <p className="font-medium text-white">Contenuto non disponibile</p>
            <p className="text-xs">Passaggio al mood successivo...</p>
          </div>
        ) : isVideo ? (
          <video
            key={story.story_id}
            ref={videoRef}
            className="story-media"
            src={getImageUrl(story.media_url)}
            poster={story.cover_url ? getImageUrl(story.cover_url) : undefined}
            style={{ filter: (story.overlays?.filter) || undefined }}
            playsInline
            autoPlay
            preload="auto"
            muted={isMuted}
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) durationRef.current = d * 1000;
            }}
            onCanPlay={() => setIsMediaReady(true)}
            onError={() => setMediaError(true)}
            data-testid="story-media-video"
          />
        ) : (
          <img
            key={story.story_id}
            className="story-media"
            src={getImageUrl(story.media_url)}
            alt=""
            style={{ filter: (story.overlays?.filter) || undefined }}
            draggable={false}
            ref={(node) => {
              if (node && node.complete && node.naturalWidth > 0) setIsMediaReady(true);
            }}
            onLoad={() => setIsMediaReady(true)}
            onError={() => setMediaError(true)}
            data-testid="story-media-image"
          />
        )}

        {/* Loading state */}
        {!isMediaReady && !mediaError && (
          <div className="story-loading" data-testid="story-loading">
            <div className="story-loading-spinner" />
          </div>
        )}

        {/* Overlay ADE Story Studio: testi, adesivi, disegni, sticker interattivi */}
        {story.overlays?.vignette && !mediaError && <div className="sst-vignette" />}
        {story.overlays?.grain && !mediaError && <div className="sst-grain" />}
        {story.overlays && !mediaError && (
          <StoryOverlayLayer
            overlays={story.overlays}
            mode="view"
            results={stickerResults.results}
            mine={stickerResults.mine}
            isOwner={stickerResults.is_owner || group.isMine}
            onInteract={handleStickerInteract}
            onOpenQuestion={(el) => { setQuestionSheet(el); setQuestionText(""); }}
          />
        )}

        {/* Readability gradients */}
        <div className="story-grad-top" />
        <div className="story-grad-bottom" />

        {/* Invisible tap/swipe/hold zones */}
        <div className="story-navigation">
          <button
            className="story-nav story-nav-left"
            aria-label="Previous story"
            data-testid="story-nav-left"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp("left")}
            onPointerCancel={handlePointerCancel}
          />
          <button
            className="story-nav story-nav-right"
            aria-label="Next story"
            data-testid="story-nav-right"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp("right")}
            onPointerCancel={handlePointerCancel}
          />
        </div>

        {/* TOP CHROME: segmented progress + header */}
        <div className="story-top">
          <div className="story-progress-row" data-testid="story-progress-row">
            {group.stories.map((s, idx) => (
              <div key={s.story_id} className="story-progress-track" data-testid={`story-progress-segment-${idx}`}>
                <div
                  className="story-progress-fill"
                  ref={idx === storyIndex ? activeFillRef : null}
                  style={{ transform: idx < storyIndex ? "scaleX(1)" : "scaleX(0)" }}
                />
              </div>
            ))}
          </div>

          <div className="story-header">
            <button className="story-identity" onClick={openProfile} data-testid="story-user-identity" aria-label={`Apri profilo di ${group.user.name}`}>
              <GoldenRingAvatar user={group.user} size="sm" showRing={true} />
              <div style={{ minWidth: 0 }}>
                <span className="story-identity-name">
                  {group.user.name}
                  <ProBadge user={group.user} size="xs" showLabel={false} />
                </span>
                <span className="story-identity-meta" data-testid="story-clip-label">
                  {formatTimeLeft(story.expires_at)}
                  {story.clip_total > 1 && ` · Clip ${story.clip_index}/${story.clip_total}`}
                  {group.stories.length > 1 && ` · ${storyIndex + 1}/${group.stories.length}`}
                </span>
              </div>
            </button>

            <div className="story-actions">
              {story.music_id && (
                <button
                  className="story-icon-btn"
                  onClick={() => setIsMuted(!isMuted)}
                  aria-label={isMuted ? "Attiva audio" : "Disattiva audio"}
                  data-testid="story-mute-btn"
                >
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              )}
              {group.isMine && (
                <button className="story-icon-btn" onClick={handleDelete} aria-label="Elimina mood" data-testid="story-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button className="story-close story-icon-btn" onClick={onClose} aria-label="Close stories" data-testid="story-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Reazione: esplosione a pieno schermo */}
        {burst && (
          <ReactionBlast
            key={burst.id}
            emoji={burst.emoji}
            onDone={() => setBurst((b) => (b && b.id === burst.id ? null : b))}
          />
        )}

        {/* FOOTER */}
        {!group.isMine ? (
          <div className="story-footer" data-testid="story-reaction-bar">
            <div className="story-reply-row" style={{ position: "relative" }}>
              <MentionSuggestions
                text={replyText}
                onPick={(u) => setReplyText((prev) => applyMention(prev, u.username))}
              />
              <input
                ref={replyInputRef}
                className="story-reply"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onFocus={() => { setReplyFocused(true); window.scrollTo(0, 0); }}
                onBlur={() => setReplyFocused(false)}
                onKeyDown={(e) => e.key === "Enter" && handleReply()}
                placeholder="Rispondi al mood..."
                data-testid="story-reply-input"
              />
              {replyText.trim() && (
                <button className="story-send-btn" onClick={handleReply} disabled={sendingReply} data-testid="story-reply-send-btn">
                  {sendingReply ? "..." : "Invia"}
                </button>
              )}
              {/* ADE Like — standalone, separate from reactions */}
              <button onClick={() => { handleReaction("ADE_LIKE"); }}
                data-testid="story-ade-like-btn"
                className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(14px)", border: adeLiked ? "1px solid rgba(249,115,22,0.6)" : "1px solid rgba(255,255,255,0.18)" }}>
                <img src="/images/ade-logo-tight.png" alt="ADE Like"
                     style={{ width: 26, height: 22, objectFit: "contain",
                       filter: adeLiked
                         ? "drop-shadow(0 0 8px rgba(249,115,22,0.7))"
                         : "grayscale(100%) brightness(1.8)" }} />
              </button>
              {/* Glass action buttons */}
              <button onClick={openComments} data-testid="story-comments-btn"
                className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(14px)", border: "1px solid rgba(255,255,255,0.18)" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
              </button>
              <button onClick={() => { setShowShare(true); }} data-testid="story-share-btn"
                className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(14px)", border: "1px solid rgba(255,255,255,0.18)" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><path d="M16 6l-4-4-4 4"/><path d="M12 2v14"/></svg>
              </button>
              <button onClick={() => setShowReactions((v) => !v)} data-testid="story-reactions-toggle"
                className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                style={{ background: showReactions ? "linear-gradient(135deg,#ec4899,#f97316)" : "rgba(8,12,24,0.5)", backdropFilter: "blur(14px)", border: showReactions ? "none" : "1px solid rgba(255,255,255,0.18)" }}>
                <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
              </button>
            </div>
            {/* On-demand reaction picker */}
            {showReactions && (
            <div className="story-reactions" data-testid="story-reactions-picker">
              {REACTION_EMOJIS.map((emoji, i) => (
                <button
                  key={emoji}
                  className={`story-reaction-btn ${poppedEmoji === emoji ? "popped" : ""}`}
                  style={{ animation: `story-pick-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.045}s both` }}
                  onClick={() => { handleReaction(emoji); setTimeout(() => setShowReactions(false), 700); }}
                  disabled={reactionSent}
                  aria-label={`Send reaction ${emoji}`}
                  data-testid={`react-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
              <style>{`@keyframes story-pick-pop { from { transform: scale(0) translateY(12px); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
            </div>
            )}
          </div>
        ) : (
          <div className="story-footer">
            <button className="story-views-btn" onClick={() => setShowViewers(true)} data-testid="story-views-btn">
              <Eye className="w-4 h-4" />
              <span>{viewCount}</span>
            </button>
          </div>
        )}

        {/* Share sheet */}
        {showShare && (
          <div className="fixed inset-0 z-[60] flex items-end" onClick={() => setShowShare(false)} data-testid="story-share-sheet">
            <div className="w-full rounded-t-3xl p-5 pb-8" onClick={(e) => e.stopPropagation()}
                 style={{ background: "rgba(10,14,28,0.85)", backdropFilter: "blur(24px)", borderTop: "1px solid rgba(255,255,255,0.16)" }}>
              <p className="text-white text-sm font-bold mb-4">Condividi storia</p>
              <div className="flex gap-3">
                {[["chat", "In chat", "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"],
                  ["copy", "Copia link", "M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71 M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"],
                  ["native", "Altre app", "M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8 M16 6l-4-4-4 4 M12 2v14"]].map(([mode, label, d]) => (
                  <button key={mode} onClick={() => shareStory(mode)} data-testid={`story-share-${mode}`}
                    className="flex-1 flex flex-col items-center gap-2 py-4 rounded-2xl text-white/85 text-xs font-medium active:scale-95 transition-transform"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{d.split(" M").map((p, i) => <path key={i} d={(i ? "M" : "") + p} />)}</svg>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Comments glass sheet */}
        {showComments && (
          <div className="fixed inset-0 z-[60] flex items-end" onClick={() => setShowComments(false)} data-testid="story-comments-sheet">
            <div className="w-full rounded-t-3xl pt-3 px-4 pb-6 max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}
                 style={{ background: "rgba(10,14,28,0.85)", backdropFilter: "blur(24px)", borderTop: "1px solid rgba(255,255,255,0.16)" }}>
              <div className="w-10 h-1 rounded-full bg-white/25 mx-auto mb-3" />
              <p className="text-white text-sm font-bold mb-3">Commenti <span className="text-white/40 font-medium">· {comments.length}</span></p>
              <div className="flex-1 overflow-y-auto space-y-3 min-h-[120px]">
                {comments.length === 0 && <p className="text-white/40 text-sm text-center py-6">Nessun commento: rompi il ghiaccio!</p>}
                {comments.map((c) => (
                  <div key={c.comment_id} className="flex gap-2.5" data-testid={`story-comment-${c.comment_id}`}>
                    <img src={getImageUrl(c.user?.picture)} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                    <div className="rounded-2xl rounded-tl-md px-3 py-2" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)" }}>
                      <p className="text-white text-xs font-bold">{c.user?.name || "Utente"}</p>
                      <p className="text-white/85 text-[13px] mt-0.5 break-words" style={{ overflowWrap: "anywhere" }}>{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                  placeholder="Scrivi un commento..." data-testid="story-comment-input"
                  className="flex-1 rounded-full px-4 py-3 text-sm text-white placeholder-white/40 outline-none"
                  style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)" }} />
                <button onClick={handleAddComment} disabled={sendingComment || !commentText.trim()} data-testid="story-comment-send"
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg,#ec4899,#f97316)" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Viewers sheet (own stories) */}
        {showViewers && (
          <div className="story-viewers-sheet" data-testid="story-viewers-sheet">
            <div className="flex items-center justify-between p-3 shrink-0">
              <p className="text-white text-sm font-bold flex items-center gap-2">
                <Eye className="w-4 h-4" /> Visualizzazioni · {viewCount}
              </p>
              <button onClick={() => setShowViewers(false)} className="p-1.5 text-white/60" aria-label="Chiudi visualizzazioni">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto px-3 pb-4 space-y-1">
              {viewersList.length === 0 && (
                <p className="text-white/40 text-sm text-center py-4">Nessuna visualizzazione ancora</p>
              )}
              {viewersList.map((v) => (
                <div key={v.viewer_id} className="flex items-center gap-2.5 p-1.5">
                  <img src={getImageUrl(v.user?.picture)} alt="" className="w-9 h-9 rounded-full object-cover" />
                  <span className="text-white text-sm font-medium flex-1">{v.user?.name || "Utente"}</span>
                  <span className="text-white/40 text-xs">
                    {v.created_at ? new Date(v.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Risposta a uno sticker Domanda */}
      {questionSheet && (
        <div className="story-question-sheet" data-testid="story-question-sheet">
          <p className="story-question-prompt">{questionSheet.prompt}</p>
          <input
            className="story-question-input"
            autoFocus
            maxLength={200}
            placeholder="Scrivi la tua risposta..."
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendQuestionAnswer(); }}
            data-testid="story-question-input"
          />
          <div className="story-question-actions">
            <button onClick={() => setQuestionSheet(null)} data-testid="story-question-cancel">Annulla</button>
            <button className="primary" onClick={sendQuestionAnswer} disabled={!questionText.trim()} data-testid="story-question-send">Invia</button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default StoryViewer;
